import { Calculator } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const Row = ({ c }) => (
  <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-3 border-b border-white/[0.05] py-1.5 text-xs" data-testid={`math-row-${c.criterion_key}`}>
    <span className="min-w-0 truncate text-slate-200">
      <span className={`mr-1.5 inline-block h-1.5 w-1.5 rounded-full ${c.criterion_group === "universal" ? "bg-blue-400" : "bg-violet-400"}`} aria-hidden="true" />
      {c.name}
    </span>
    <span className="font-mono text-slate-300">{c.score.toFixed(1)}</span>
    <span className="font-mono text-slate-500">×{c.normalized_weight.toFixed(2)}</span>
    <span className="w-12 text-right font-mono text-white">{c.weighted_contribution.toFixed(2)}</span>
  </div>
);

export const MathDialog = ({ report }) => {
  const scores = report?.criteria_scores ?? [];
  const total = scores.reduce((s, c) => s + c.weighted_contribution, 0);
  const ev = report?.evaluation;
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button type="button" disabled={!report} className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-300 underline-offset-4 hover:underline disabled:opacity-40" data-testid="verify-math-link">
          <Calculator className="h-3.5 w-3.5" aria-hidden="true" /> Verify Deterministic Math
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-[calc(100vw-2rem)] border-white/10 bg-[#0B1021] text-white sm:max-w-xl" data-testid="math-dialog">
        <DialogHeader>
          <DialogTitle>Deterministic Score Math — {report?.idea?.title}</DialogTitle>
          <DialogDescription className="text-slate-400">
            FinalValidationScore = ROUND(Σ score₀₋₁₀ × normalised weight₀₋₁₀₀ ÷ 10, 1). The 20 active weights are normalised to 100.
          </DialogDescription>
        </DialogHeader>
        <div className="noscrollbar max-h-[55vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 pb-1 text-[10px] uppercase tracking-wider text-slate-500">
            <span>Criterion</span><span>Score</span><span>Weight</span><span className="w-12 text-right">Points</span>
          </div>
          {scores.map((c) => <Row key={c.criterion_key} c={c} />)}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-3 text-sm">
          <span className="text-slate-300">Σ points = <span className="font-mono text-white" data-testid="math-total">{total.toFixed(1)}</span></span>
          <span className="text-slate-300">Verdict: <span className="font-semibold text-white">{ev?.verdict_label}</span></span>
        </div>
        {ev?.fatal_flaws?.length > 0 && (
          <p className="text-xs text-rose-300" data-testid="math-fatal-flaws">
            Fatal-flaw override: {ev.fatal_flaws.map((f) => f.message).join(" · ")}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};
